/*
 This file is part of GNU Taler
 (C) 2021-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import {
  CurrencySpecification,
  MerchantAccountKycStatus,
  TalerError,
} from "@gnu-taler/taler-util";
import { ComponentChildren, createContext, h, VNode } from "preact";
import { useContext } from "preact/hooks";
import { useSessionContext } from "./session.js";
import { useState } from "preact/hooks";
import {
  useAvailableCurrencies,
  useInstanceKYCDetails,
} from "../hooks/instance.js";

/**
 *
 * @author Sebastian Javier Marchano (sebasjm)
 */
type CurrencyWithId = undefined | (CurrencySpecification & { id: string });
export type Type = {
  list: string[];
  currency: CurrencyWithId;
  switchCurrency: (id: string) => void;
};

const initial: Type = {
  list: [],
  currency: undefined,
  switchCurrency(id) {},
};
const Context = createContext<Type>(initial);

export const useCurrenciesContext = (): Type => useContext(Context);

export const CurrenciesProvider = ({
  children,
}: {
  children: ComponentChildren;
}): VNode => {
  const { config } = useSessionContext();
  const [currency, setCurrency] = useState<
    undefined | (CurrencySpecification & { id: string })
  >();

  const kyc = useInstanceKYCDetails();

  const status =
    kyc instanceof TalerError || !kyc || kyc.type === "fail"
      ? undefined
      : kyc.body;

  const readyToBeUsed = !status
    ? []
    : status.kyc_data
        .map((st) =>
          st.status === MerchantAccountKycStatus.READY
            ? st.exchange_currency
            : undefined,
        )
        .filter((d): d is string => !!d);

  const firstCurrency = !readyToBeUsed.length ? undefined : readyToBeUsed[0];
  const fromDefault = !firstCurrency
    ? undefined
    : {
        ...config.currencies[firstCurrency],
        id: firstCurrency,
      };

  return h(Context.Provider, {
    value: {
      list: readyToBeUsed,
      currency: currency ?? fromDefault,
      switchCurrency(id: string) {
        const def = config.currencies[id];
        if (!def) return;
        setCurrency({
          ...def,
          id: id,
        });
      },
    },
    children,
  });
};
